import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  MessageCircle, 
  Mic, 
  FileText, 
  CheckSquare, 
  ArrowRight, 
  ArrowLeft,
  Clock,
  Brain,
  Heart,
  Zap
} from 'lucide-react'
import { VoiceAssessment } from './VoiceAssessment'
import { TextAssessment } from './TextAssessment'
import { MCQAssessment } from './MCQAssessment'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { getUserSubscription } from '../../lib/stripe'
import toast from 'react-hot-toast'

interface MultiTypeAssessmentProps {
  employeeId: string
  onComplete: (results: any) => void
}

type AssessmentType = 'voice' | 'text' | 'mcq'

export const MultiTypeAssessment: React.FC<MultiTypeAssessmentProps> = ({
  employeeId,
  onComplete
}) => {
  const [selectedType, setSelectedType] = useState<AssessmentType | null>(null)
  const [assessmentStarted, setAssessmentStarted] = useState(false)
  const [subscription, setSubscription] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  React.useEffect(() => {
    const loadSubscription = async () => {
      try {
        setLoading(true)
        const { subscription: subData } = await getUserSubscription()
        setSubscription(subData)
      } catch (error) {
        console.error('Error loading subscription:', error)
      } finally {
        setLoading(false)
      }
    }
    
    loadSubscription()
  }, [])

  const assessmentTypes = [
    {
      type: 'voice' as AssessmentType,
      title: 'Voice Assessment',
      description: 'Have a natural conversation with our AI wellness assistant',
      icon: Mic,
      duration: '3-5 minutes',
      features: ['Natural conversation', 'Voice sentiment analysis', 'Real-time insights'],
      color: 'from-sage-500 to-emerald-500',
      premium: true
    },
    {
      type: 'text' as AssessmentType,
      title: 'Text Assessment',
      description: 'Answer wellness questions through written responses',
      icon: FileText,
      duration: '5-7 minutes',
      features: ['Written responses', 'Detailed analysis', 'Flexible timing'],
      color: 'from-blue-500 to-blue-600',
      premium: true
    },
    {
      type: 'mcq' as AssessmentType,
      title: 'Quick Survey',
      description: 'Multiple choice questions for quick wellness check',
      icon: CheckSquare,
      duration: '2-3 minutes',
      features: ['Quick completion', 'Standardized metrics', 'Easy tracking'],
      color: 'from-purple-500 to-purple-600',
      premium: false
    }
  ]

  const handleTypeSelection = (type: AssessmentType) => {
    // Check if premium feature and user has subscription
    const assessmentType = assessmentTypes.find(a => a.type === type)
    
    if (assessmentType?.premium && 
        (!subscription || 
         (subscription.subscription_status !== 'active' && 
          subscription.subscription_status !== 'trialing'))) {
      
      // Check if it's the Professional plan specifically
      if (!subscription?.product?.name?.toLowerCase().includes('professional')) {
        toast.error('This assessment type requires a Professional subscription')
        return
      }
    }
    
    setSelectedType(type)
    setAssessmentStarted(true)
  }

  const handleAssessmentComplete = (results: any) => {
    const enhancedResults = {
      ...results,
      assessment_type: selectedType,
      employee_id: employeeId,
      completed_at: new Date().toISOString()
    }
    onComplete(enhancedResults)
  }

  const goBack = () => {
    setAssessmentStarted(false)
    setSelectedType(null)
  }

  if (assessmentStarted && selectedType) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={goBack}
            className="text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Assessment Types
          </Button>
        </div>

        <AnimatePresence mode="wait">
          {selectedType === 'voice' && (
            <motion.div
              key="voice"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <VoiceAssessment
                employeeId={employeeId}
                onComplete={handleAssessmentComplete}
              />
            </motion.div>
          )}

          {selectedType === 'text' && (
            <motion.div
              key="text"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <TextAssessment
                employeeId={employeeId}
                onComplete={handleAssessmentComplete}
              />
            </motion.div>
          )}

          {selectedType === 'mcq' && (
            <motion.div
              key="mcq"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <MCQAssessment
                employeeId={employeeId}
                onComplete={handleAssessmentComplete}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    )
  }

  const isProfessionalSubscriber = subscription?.product?.name?.toLowerCase().includes('professional') &&
                                  (subscription.subscription_status === 'active' || 
                                   subscription.subscription_status === 'trialing')

  return (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-slate-900 mb-4">
          Choose Your Assessment Type
        </h1>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
          Select the assessment format that feels most comfortable for you. 
          All options provide valuable insights into your wellbeing.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {assessmentTypes.map((assessment) => (
          <motion.div
            key={assessment.type}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: assessmentTypes.indexOf(assessment) * 0.1 }}
            whileHover={{ y: -5 }}
            className={assessment.premium && !isProfessionalSubscriber ? "cursor-not-allowed opacity-75" : "cursor-pointer"}
          >
            <Card className="p-8 text-center h-full border-2 border-transparent hover:border-slate-200 transition-all duration-300 hover:shadow-lg">
              <div className={`w-20 h-20 bg-gradient-to-r ${assessment.color} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                <assessment.icon className="w-10 h-10 text-white" />
              </div>
              
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                {assessment.title}
                {assessment.premium && !isProfessionalSubscriber && (
                  <span className="ml-2 px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                    Premium
                  </span>
                )}
              </h3>
              
              <p className="text-slate-600 mb-4 leading-relaxed">
                {assessment.description}
              </p>

              <div className="flex items-center justify-center mb-4 text-sm text-slate-500">
                <Clock className="w-4 h-4 mr-2" />
                <span>{assessment.duration}</span>
              </div>

              <div className="space-y-2 mb-6">
                {assessment.features.map((feature, index) => (
                  <div key={index} className="flex items-center text-sm text-slate-600">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              <Button
                className={`w-full bg-gradient-to-r ${assessment.color} text-white hover:opacity-90`}
                onClick={() => handleTypeSelection(assessment.type)}
                disabled={assessment.premium && !isProfessionalSubscriber}
              >
                {assessment.premium && !isProfessionalSubscriber ? (
                  <>Requires Professional</>
                ) : (
                  <>
                    Start Assessment
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Subscription Upsell */}
      {!isProfessionalSubscriber && (
        <div className="mt-12 text-center">
          <Card className="p-8 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200 max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-4">
              <Brain className="w-8 h-8 text-blue-600 mr-3" />
              <Zap className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-3">
              Unlock Advanced Assessment Features
            </h3>
            <p className="text-slate-600 max-w-2xl mx-auto mb-6">
              Upgrade to our Professional plan to access voice and text assessments with advanced AI analysis, 
              personalized insights, and comprehensive wellness tracking.
            </p>
            <Button
              onClick={() => window.location.href = '/settings?tab=subscription'}
              className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
            >
              Upgrade to Professional
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Card>
        </div>
      )}

      <div className="mt-12 text-center">
        <div className="bg-sage-50 rounded-2xl p-8 max-w-4xl mx-auto">
          <div className="flex items-center justify-center mb-4">
            <Heart className="w-8 h-8 text-sage-600 mr-3" />
            <Brain className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">
            Your Privacy & Wellbeing Matter
          </h3>
          <p className="text-slate-600 max-w-2xl mx-auto">
            All assessments are confidential and designed to support your mental health. 
            Your responses help create a better workplace environment for everyone.
          </p>
          <div className="flex items-center justify-center space-x-8 mt-6 text-sm text-slate-500">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-sage-500 rounded-full mr-2" />
              <span>HIPAA Compliant</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2" />
              <span>End-to-End Encrypted</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-emerald-500 rounded-full mr-2" />
              <span>Anonymized Analytics</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}